import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { closeDeleteDocumentModal } from '@/redux/collections/actions';
import {
  selectDeleteDocumentTarget,
  selectIsDeleteDocumentModalOpen,
} from '@/redux/collections/selectors';
import { setDatasourceId, setDocumentId } from '@/redux/session/actions';
import { selectDocumentId } from '@/redux/session/selectors';
import { setSidebarContent } from '@/redux/sidebar/actions';

import { useAPIDocuments } from '../hooks/useAPIDocuments';

export const DeleteDocumentModal = () => {
  const { collectionId } = useParams();
  // The row the menu was opened on, not the row a right panel is pointing at.
  const target = useSelector(selectDeleteDocumentTarget);
  const selectedDocumentId = useSelector(selectDocumentId);
  // Custom hooks
  const { t } = useTranslation();
  const open = useSelector(selectIsDeleteDocumentModalOpen);
  const { document, deleteDocument, deleteStatus } = useAPIDocuments({
    collection_id: collectionId,
    datasource_id: target?.datasource_id,
    document_id: target?.document_id,
    enabled: open,
  });
  const dispatch = useDispatch();
  const isDeleting = deleteStatus === 'pending';

  const closeModal = () => {
    dispatch(closeDeleteDocumentModal());
  };

  const handleDeleteDocument = () => {
    deleteDocument(
      { document_id: target?.document_id },
      {
        onSuccess: () => {
          closeModal();
          // Only the panel showing the deleted document is stale; one open on another row keeps
          // its selection.
          if (selectedDocumentId === target?.document_id) {
            dispatch(setDocumentId(null));
            dispatch(setDatasourceId(null));
            dispatch(setSidebarContent(null));
          }
        },
      }
    );
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      closeModal();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="gap-6 sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('collections.modals.delete_content_title')}</DialogTitle>
          <DialogDescription>
            <Trans
              i18nKey={t('collections.modals.delete_content_subtitle')}
              values={[document?.name]}
            />
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-end gap-2">
          <Button
            variant="ghost-neutral"
            onClick={(e) => {
              // Prevent the event from bubbling up to the parent
              // and open the sidebar when it is closed
              e.stopPropagation();
              closeModal();
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            className="text-destructive"
            onClick={(e) => {
              // Prevent the event from bubbling up to the parent
              // and open the sidebar when it is closed
              e.stopPropagation();
              handleDeleteDocument();
            }}
            isLoading={isDeleting}
          >
            {t('common.delete')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
